import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    VStack,
    Box,
    Text,
    List,
    ListItem,
    ListIcon,
    Divider,
    Button,
    Alert,
    AlertIcon,
  } from '@chakra-ui/react';
  import { MdAdd, MdEdit, MdDelete, MdCheck } from 'react-icons/md';
  import { TaskChanges } from '@/lib/sdk/chatbot/types';

  interface UpdateDialogProps {
    isOpen: boolean;
    onClose: () => void;
    changes: TaskChanges[];
    /** True when this chatbot has a Published WhatsApp Flow — its JSON is frozen at
     * publish time, so these task changes won't reach it until someone deprecates and
     * re-publishes from the workflow's Flow tab. */
    flowPublished?: boolean;
  }

  export const UpdateDialog = ({ isOpen, onClose, changes, flowPublished }: UpdateDialogProps) => {
    // Group changes by type
    const addedTasks = changes.filter(task => task.added);
    const editedTasks = changes.filter(task => task.edited);
    const deletedTasks = changes.filter(task => task.deleted);
  
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent bg="bg-surface">
          <ModalHeader color="gray.700">Task Changes Summary</ModalHeader>
          <ModalCloseButton color="gray.700" />
          
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {flowPublished && changes.length > 0 && (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  This workflow's WhatsApp Flow is published, so it won't pick up these
                  task changes on its own — its screens are frozen from when it was last
                  published. Deprecate and re-publish the Flow from its Flow tab to bring
                  it in sync.
                </Alert>
              )}

              {/* Added Tasks */}
              {addedTasks.length > 0 && (
                <Box>
                  <Text
                    fontSize="md"
                    fontWeight="semibold"
                    color="gray.700"
                    mb={2}
                  >
                    {addedTasks.length === changes.length 
                      ? `Added ${addedTasks.length} new task${addedTasks.length > 1 ? 's' : ''}`
                      : 'Added Tasks'
                    }
                  </Text>
                  <List spacing={2}>
                    {addedTasks.map(task => (
                      <ListItem 
                        key={task.id}
                        display="flex"
                        alignItems="center"
                        color="gray.600"
                      >
                        <ListIcon as={MdAdd} color="green.500" />
                        {task.name}
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
  
              {/* Divider between sections if multiple types exist */}
              {addedTasks.length > 0 && (editedTasks.length > 0 || deletedTasks.length > 0) && (
                <Divider borderColor="border-default" opacity={0.5} />
              )}
  
              {/* Edited Tasks */}
              {editedTasks.length > 0 && (
                <Box>
                  <Text
                    fontSize="md"
                    fontWeight="semibold"
                    color="gray.700"
                    mb={2}
                  >
                    Modified Tasks
                  </Text>
                  <List spacing={2}>
                    {editedTasks.map(task => (
                      <ListItem 
                        key={task.id}
                        display="flex"
                        alignItems="center"
                        color="gray.600"
                      >
                        <ListIcon as={MdEdit} color="blue.500" />
                        {task.name}
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
  
              {/* Divider before deleted tasks if they exist */}
              {editedTasks.length > 0 && deletedTasks.length > 0 && (
                <Divider borderColor="border-default" opacity={0.5} />
              )}
  
              {/* Deleted Tasks */}
              {deletedTasks.length > 0 && (
                <Box>
                  <Text
                    fontSize="md"
                    fontWeight="semibold"
                    color="gray.700"
                    mb={2}
                  >
                    Deleted Tasks
                  </Text>
                  <List spacing={2}>
                    {deletedTasks.map(task => (
                      <ListItem 
                        key={task.id}
                        display="flex"
                        alignItems="center"
                        color="gray.600"
                      >
                        <ListIcon as={MdDelete} color="red.500" />
                        {task.name}
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
  
              {/* No changes message */}
              {changes.length === 0 && (
                <Text color="gray.600" textAlign="center">
                  No changes were made to the tasks.
                </Text>
              )}
            </VStack>
          </ModalBody>
  
          <ModalFooter>
            <Button
              colorScheme="brand"
              leftIcon={<MdCheck />}
              onClick={onClose}
              size="md"
              width={{ base: "full", sm: "auto" }}
            >
              Ok
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  };